import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { ControlButtons } from '../ControlButtons';
import { blastingTypeSchema } from '../../schemas/blastingTypeSchema';
import dayjs from 'dayjs';

const formatDate = rawDate =>
	!!rawDate ? dayjs(rawDate).format('MM/DD/YYYY') : '';

const getDefaultVals = data => {
	const defaultValues = {};
	Object.keys(data).forEach(key => {
		if (!!key.match(/date/i) && !!data[key]) {
			// Need to reformat to ISO format so the input will understand the date
			defaultValues[key] = dayjs(data[key]).format('YYYY-MM-DD');
		} else {
			defaultValues[key] = data[key];
		}
	});
	return defaultValues;
};

export const BlastingType = ({
	blastingType = {},
	onNext = () => {},
	onPrevious = () => {},
	onSubmit = () => {},
	finalStep = false,
	disablePrev = false,
}) => {
	const { register, handleSubmit, formState, setValue, watch } = useForm({
		resolver: yupResolver(blastingTypeSchema),
		defaultValues: getDefaultVals(blastingType),
	});

	const registerDateField = (name, opts) =>
		register(name, { ...opts, setValueAs: formatDate });

	const permitType = watch('permitType');

	useEffect(() => {
		const subscription = watch((data, { name }) => {
			if (name === 'permitType') {
				switch (data.permitType) {
					case 'single':
						setValue('startDate', '');
						setValue('endDate', '');
						break;

					case 'annual':
						setValue('date', '');
						break;

					default:
						break;
				}
			}
		});
		return () => subscription.unsubscribe();
	}, [watch, setValue]);

	return (
		<Container className="component mb-2">
			<Container className="component mt-4">
				<h2 className="mb-4">Blasting Permit Type</h2>
				<Row className="mb-4 d-flex">
					<Col xs="12" md="6">
						<Card
							onClick={() => setValue('permitType', 'single')}
							style={
								permitType === 'single' ? { border: 'solid #0074d9 2px' } : {}
							}>
							<Card.Body>
								<Card.Title>Single Blasting Event</Card.Title>
								<Form.Check
									className="noRadio"
									name="permitType"
									type="radio"
									value="single"
									{...register('permitType')}
									checked={permitType === 'single'}
								/>
							</Card.Body>
						</Card>
					</Col>
					<Col xs="12" md="6">
						<Card
							onClick={() => setValue('permitType', 'annual')}
							style={
								permitType === 'annual' ? { border: 'solid #0074d9 2px' } : {}
							}>
							<Card.Body>
								<Card.Title>Annual Blasting Permit</Card.Title>
								<Form.Check
									className="noRadio"
									name="permitType"
									type="radio"
									value="annual"
									{...register('permitType')}
									checked={permitType !== 'single'}
								/>
							</Card.Body>
						</Card>
					</Col>
				</Row>
				<Form.Text className="text-danger">
					{formState.errors.permitType?.message}
				</Form.Text>
				{permitType === 'single' && (
					<Form.Group>
						<Form.Label>Proposed Date</Form.Label>
						<Form.Control type="date" {...registerDateField('date')} />
						<Form.Text className="text-danger">
							{formState.errors.date?.message}
						</Form.Text>
					</Form.Group>
				)}
				{permitType === 'annual' && (
					<div>
						<Form.Label>Proposed Date Range</Form.Label>
						<Row>
							<Form.Group as={Col}>
								<Form.Control type="date" {...registerDateField('startDate')} />
								<Form.Text className="text-danger">
									{formState.errors.startDate?.message}
								</Form.Text>
							</Form.Group>
							to
							<Form.Group as={Col}>
								<Form.Control type="date" {...registerDateField('endDate')} />
								<Form.Text className="text-danger">
									{formState.errors.endDate?.message}
								</Form.Text>
							</Form.Group>
						</Row>
					</div>
				)}
			</Container>
			<ControlButtons
				onNext={() => handleSubmit(onNext)()}
				onPrev={onPrevious}
				onSubmit={onSubmit}
				disablePrev={disablePrev}
				disableNext={permitType ? false : true}
				finalStep={finalStep}
			/>
		</Container>
	);
};
